export interface DetailRow {
  readonly label: string;
  readonly value: string;
}

export interface DetailListProps {
  readonly rows: readonly DetailRow[];
}

/**
 * The label/value rows of the status card [Figma · 14 · Active associate, `12021:41217`].
 *
 * A description list, not a table of divs: the pairing IS the meaning, and `<dl>` is the element that says
 * so to a screen reader. Each row is 48px with a 16px inset, a hairline above every row after the first, and
 * the value right-aligned and bold against a muted label — read off the frame rather than chosen.
 */
export function DetailList({ rows }: DetailListProps) {
  return (
    /*
      An inset ring, not a `border`. The frame draws Status as 192 tall — four 48px rows — with its outline
      INSIDE that box. A CSS border on an auto-height element adds to the height instead, so the list came
      out 194 and pushed the callout below it 3px off. `box-shadow: inset` paints the same 1px line and
      occupies no space, which is what the frame actually depicts.
    */
    <dl className="flex w-full flex-col rounded-[var(--radius-md)] bg-[var(--card)] shadow-[inset_0_0_0_1px_var(--divider-border)]">
      {rows.map((row, index) => (
        <div
          className={`flex h-12 w-full items-center justify-between gap-4 px-4 py-3 ${
            index === 0 ? '' : 'border-t border-[var(--divider-border)]'
          }`}
          key={row.label}
        >
          <dt className="shrink-0 text-sm leading-5 text-[var(--muted-foreground)]">{row.label}</dt>
          {/*
            `text-right` and `min-w-0` together: the address is the longest value on the card and the frame
            keeps it on one line at 820. Letting it push the label instead of wrapping would break the row
            grid on every screen below that.
          */}
          <dd className="min-w-0 text-right text-sm font-bold leading-5 text-[var(--foreground)]">
            {row.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}
